// ── Notifications Modal (C1351, row-click/redesign TPT22) ──
//
// Shows the caller's ENTIRE cross-project notification list — deliberately unfiltered by
// their digest_mode/transactional_mode settings (Settings ▸ Notifications, C1350): those
// only gate what gets emailed, never what this table shows (see tt-api-notifications.md).
// Data comes from GET /api/workspace/notifications (lib/notifications.js's
// fetchNotifications()); mark-read goes through the same module's markNotificationReadOnce(),
// which republishes the shared unread count to every subscriber (sidebar badge + dashboard
// button badge both update without a full re-render of either).
//
// Whole-row click contract (TPT22): the entire <tr> is one target — click anywhere on a row
// with a task to mark it read, close this modal, and open the task page. A row with no task
// (a digest notification) just marks read and leaves the modal open, since there's nowhere to
// navigate. Scrolling a row into view inside the modal counts as "read" too (a seen-observer,
// modelled on task-detail.js's Notifications-tab IntersectionObserver, TPT18) — a long list
// drains both unread badges as the user scrolls it, not only on click.

import { openModal, closeModal, showToast, esc, projectSwatchColor } from '../ui.js';
import { formatDate } from '../utils.js';
import { fetchNotifications, markNotificationReadOnce } from '../lib/notifications.js';
import { navigate } from '../router.js';

const PAGE_SIZE = 50;
const SEEN_THRESHOLD = 0.5; // a row counts as "viewed" once half of it is in the modal's viewport
const SEEN_CLEAR_DELAY_MS = 1500; // (TPT97) how long a scrolled-into-view row holds its unread
  // look before fading to read — long enough to notice, short enough that a user working down
  // the list watches rows drain instead of the whole list flattening instantly on open

function renderNotifRow(n) {
  const unread = !n.read_at;
  // No color_scheme on a notification row (GET /workspace/notifications doesn't join it) —
  // projectSwatchColor() falls back to its per-project-id hash color the same way
  // workspace-tasks.js's rows do before that page's own project list has loaded.
  const color = projectSwatchColor(undefined, n.project_id);
  const taskCell = n.task_key
    ? `<a class="task-key-link" href="#/projects/${n.project_id}/tasks/${encodeURIComponent(n.task_key)}" draggable="false" title="Open task page">${esc(n.task_key)}</a>`
    : '<span class="text-muted">—</span>';
  // .ws-row supplies the shared hover background + pointer cursor (styles.css) — the same
  // idiom every other clickable table row in this app uses.
  return `<tr class="notif-row ws-row${unread ? ' unread' : ''}" data-id="${n.id}" data-project-id="${n.project_id}">
    <td class="notif-cell-dot"><span class="notif-dot" ${unread ? '' : 'hidden'} title="Unread"></span></td>
    <td class="notif-cell-title">${esc(n.title)}</td>
    <td class="notif-cell-project"><span class="ws-project-badge" style="--proj-color:${color}">${esc(n.project_name || '')}</span></td>
    <td class="notif-cell-task">${taskCell}</td>
    <td class="text-muted ws-cell-date">${formatDate(n.created_at)}</td>
  </tr>`;
}

// renderNotificationsTable(rows) — the reusable table markup used by openNotificationsModal()'s
// initial render and its subsequent Load More appends. table-layout:fixed (styles.css) takes
// its column widths from these <th> classes, so they must stay in sync with the <td> classes
// above.
export function renderNotificationsTable(rows) {
  if (!rows.length) {
    return '<div class="empty text-muted">No notifications</div>';
  }
  return `<table class="ws-table notif-table"><thead><tr>
      <th class="notif-col-dot"></th><th class="notif-col-title">Title</th><th class="notif-col-project">Project</th><th class="notif-col-task">Task</th><th class="notif-col-when">When</th>
    </tr></thead><tbody id="notif-tbody">${rows.map(renderNotifRow).join('')}</tbody></table>`;
}

export function openNotificationsModal() {
  let rows = [];
  let total = 0;
  let offset = 0;
  let loading = false;
  let seenObserver = null;
  // (TPT97) id -> setTimeout id, for rows the seen-observer PATCHed read but whose unread
  // visual is still being held for SEEN_CLEAR_DELAY_MS. Declared here (not inside onMount) so
  // onClose can also reach it. Cleared on modal close and on a click that clears the same
  // row's visual early.
  const pendingClears = new Map();

  const body = `
    <div id="notif-list"><div class="empty"><span class="spinner"></span></div></div>
    <div id="notif-pagination"></div>`;

  openModal('Notifications', body, {
    onMount(overlay) {
      const modalEl = overlay.querySelector('.modal');
      if (modalEl) modalEl.classList.add('modal-wide'); // room for a single-line title (TPT22)
      const listEl = overlay.querySelector('#notif-list');
      const paginationEl = overlay.querySelector('#notif-pagination');

      function renderPagination() {
        if (!rows.length) { paginationEl.innerHTML = ''; return; }
        paginationEl.innerHTML = `<div class="ws-pagination">
          <span class="text-muted">Showing ${rows.length} of ${total} notifications</span>
          ${rows.length < total ? '<button class="btn btn-ghost btn-sm" id="notif-load-more">Load More</button>' : ''}
        </div>`;
        const btn = paginationEl.querySelector('#notif-load-more');
        if (btn) btn.addEventListener('click', () => { offset += PAGE_SIZE; load(true); });
      }

      // clearUnreadVisual(row, localRow) — drops the .unread class + hides the dot, i.e. the
      // part of markRowRead that used to run synchronously. Guarded on row.isConnected: a Load
      // More append replaces #notif-tbody wholesale, so a deferred clear (see delay below) can
      // fire after its row is long gone from the DOM.
      function clearUnreadVisual(row, localRow) {
        if (localRow) localRow.read_at = new Date().toISOString();
        if (!row.isConnected) return;
        row.classList.remove('unread');
        const dot = row.querySelector('.notif-dot');
        if (dot) dot.hidden = true;
      }

      // markRowRead(row, id, projectId, localRow, opts) — shared by the click handler and the
      // seen-observer below. Fires the PATCH un-awaited (a caller navigating away must never
      // wait on it) so the bell badge always drains immediately. `delay` controls when the
      // *visual* (the .unread class/tint/dot) actually clears (TPT97):
      //   - 0 (row click): clear at once — today's behavior, the row is about to disappear
      //     under a navigation anyway.
      //   - >0 (seen-observer): hold the unread look for `delay` ms so a user scrolling through
      //     the list watches rows fade to read one at a time instead of the whole list
      //     flattening the instant the modal opens. The pending timer is tracked in
      //     `pendingClears` so onClose() can cancel it and a same-row click can jump the queue.
      // On failure, restores the DOM only if the row is still attached (it won't be after we've
      // navigated away) and always reverts the localRow's read_at so a later Load More
      // re-render doesn't repaint it as read. markNotificationReadOnce() (lib/notifications.js)
      // dedupes the click and the observer racing for the same row.
      function markRowRead(row, id, projectId, localRow, { delay = 0 } = {}) {
        if (!row.classList.contains('unread')) return;
        const key = String(id);
        const pending = pendingClears.get(key);
        if (pending != null) { clearTimeout(pending); pendingClears.delete(key); }

        if (delay > 0) {
          pendingClears.set(key, setTimeout(() => {
            pendingClears.delete(key);
            clearUnreadVisual(row, localRow);
          }, delay));
        } else {
          clearUnreadVisual(row, localRow);
        }

        markNotificationReadOnce(projectId, id).catch((err) => {
          showToast(err.message || 'Failed to mark read', 'error');
          const stillPending = pendingClears.get(key);
          if (stillPending != null) { clearTimeout(stillPending); pendingClears.delete(key); }
          if (row.isConnected) {
            row.classList.add('unread');
            const dot = row.querySelector('.notif-dot');
            if (dot) dot.hidden = false;
          }
          if (localRow) localRow.read_at = null;
        });
      }

      function disconnectSeenObserver() {
        if (seenObserver) { seenObserver.disconnect(); seenObserver = null; }
      }

      // observeUnreadRows() — (TPT22) re-armed after every render (initial load and each Load
      // More append both replace #notif-tbody, discarding any previously observed nodes).
      // Watches only the currently-unread rows; a row is unobserved the moment it's marked
      // read, whether that happens here or via a row click that beat the scroll to it.
      function observeUnreadRows() {
        disconnectSeenObserver();
        if (!modalEl) return;
        const unreadRows = listEl.querySelectorAll('.notif-row.unread');
        if (!unreadRows.length) return;
        seenObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const row = entry.target;
            seenObserver.unobserve(row);
            if (!row.classList.contains('unread')) continue;
            markRowRead(row, row.dataset.id, row.dataset.projectId, rows.find(r => String(r.id) === row.dataset.id), { delay: SEEN_CLEAR_DELAY_MS });
          }
        }, { root: modalEl, threshold: SEEN_THRESHOLD });
        unreadRows.forEach(row => seenObserver.observe(row));
      }

      async function load(append) {
        if (loading) return;
        loading = true;
        if (!append) {
          offset = 0;
          listEl.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
        }
        try {
          const res = await fetchNotifications({ limit: PAGE_SIZE, offset });
          rows = append ? [...rows, ...res.notifications] : res.notifications;
          total = res.total;
          listEl.innerHTML = renderNotificationsTable(rows);
          observeUnreadRows();
        } catch (err) {
          listEl.innerHTML = '<div class="empty text-muted">Failed to load notifications</div>';
          showToast(err.message || 'Failed to load notifications', 'error');
        }
        loading = false;
        renderPagination();
      }

      // Whole row is one click target (TPT22). A plain click anywhere — including the
      // task-key link — marks the row read, closes this modal, and navigates via the link's
      // own href, so the URL lives in exactly one place (the rendered <a>). A modifier-click
      // on the link itself (cmd/ctrl/shift/alt) is left alone so the browser can open its own
      // new tab; the row still gets marked read either way. A row with no task-key link (a
      // digest notification) just marks read and stays open — nothing to navigate to.
      listEl.addEventListener('click', (e) => {
        const row = e.target.closest('.notif-row');
        if (!row) return;

        const id = row.dataset.id;
        const projectId = row.dataset.projectId;
        const local = rows.find(r => String(r.id) === id);
        const link = row.querySelector('.task-key-link');
        const clickedLink = e.target.closest('a');
        const modifierHeld = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

        markRowRead(row, id, projectId, local);

        if (clickedLink && modifierHeld) return; // let the browser open its own new tab
        if (!link) return; // digest row — nothing to open, stay put

        e.preventDefault();
        closeModal();
        navigate(link.getAttribute('href').slice(1));
      });

      load(false);
    },
    onClose() {
      // seenObserver is a closure var initialized above onMount's scope; onMount always runs
      // before onClose can fire, so this is never called with it uninitialized.
      if (seenObserver) seenObserver.disconnect();
      // (TPT97) drop any in-flight fade timers so they never fire against a closed modal's
      // detached rows.
      pendingClears.forEach(clearTimeout);
      pendingClears.clear();
    },
  });
}
